// LiveScan: source chips tick off one by one, finding rows insert at the top (fade-up 12 px, 30 ms stagger),
// the counter ticks every 150 ms, and the score lands last. `compact` sits inline (Watchlist); full fills the screen (/scan).
// The results are already known (the API returned them); this paces their reveal so the user sees the scan work.
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useState, type ReactNode } from 'react';
import { PLACE_LABEL } from '../../domain/copy';
import { placeOf } from '../../domain/places';
import { band, BAND_LABEL } from '../../domain/score';
import type { Finding } from '../../domain/types';
import { Icon } from '../icons';
import { easeOut, fadeOnly, stagger } from '../styles/motion';
import { SeverityBadge } from './findingBits';
import s from './LiveScan.module.css';

interface Props {
  title: string;
  sources: string[];
  findings: Finding[];
  scoreBefore?: number;
  scoreAfter: number;
  variant?: 'compact' | 'full';
  footer?: ReactNode;      // shown when done
  onDone?: () => void;
  maxRows?: number;        // show only the newest rows (the counter still counts all)
}

const CHIP_MS = 450;
const TICK_MS = 150;

export function LiveScan({ title, sources, findings, scoreBefore, scoreAfter, variant = 'compact', footer, onDone, maxRows }: Props) {
  const reduced = useReducedMotion();
  const [chips, setChips] = useState(reduced ? sources.length : 0);
  const [shown, setShown] = useState(reduced ? findings.length : 0);
  const [count, setCount] = useState(reduced ? findings.length : 0);
  const done = chips >= sources.length && shown >= findings.length && count >= findings.length;

  useEffect(() => {
    if (reduced) return;
    const t = setInterval(() => setChips((c) => Math.min(sources.length, c + 1)), CHIP_MS);
    return () => clearInterval(t);
  }, [sources.length, reduced]);
  useEffect(() => {
    if (reduced) return;
    const t = setInterval(() => {
      setCount((c) => Math.min(findings.length, c + 1));
      setShown((c) => Math.min(findings.length, c + 1));
    }, TICK_MS);
    return () => clearInterval(t);
  }, [findings.length, reduced]);
  useEffect(() => { if (done) onDone?.(); }, [done, onDone]);

  const rows = findings.slice(0, shown).reverse().slice(0, maxRows ?? findings.length); // newest at the top
  return (
    <section className={`${s.scan} ${s[variant]}`} data-testid="live-scan" data-done={done} data-count={count} aria-live="polite" aria-busy={!done}>
      <div className={s.head}>
        <span className={`${s.pulse} ${done ? s.still : ''}`} aria-hidden><Icon name={done ? 'circle-check' : 'magnifying-glass'} size={variant === 'full' ? 24 : 20} /></span>
        <div className={s.headText}>
          <h2 className={s.title}>{done ? 'Scan complete' : title}</h2>
          <p className={`bodySm ${s.muted}`} data-testid="live-scan-count">
            <span className={s.counter}>{count}</span> {count === 1 ? 'finding' : 'findings'} {done ? 'found' : 'so far'}
          </p>
        </div>
      </div>

      <ul className={s.chips} aria-label="Sources">
        {sources.map((src, i) => (
          <li key={src} className={`bodyXsMedium ${s.chip} ${i < chips ? s.ticked : ''}`} data-testid="scan-chip" data-done={i < chips}>
            {i < chips ? <Icon name="circle-check" size={16} /> : <span className={s.spinner} aria-hidden />} {src}
          </li>
        ))}
      </ul>

      <ul className={s.rows}>
        <AnimatePresence initial={false}>
          {rows.map((f, i) => (
            <motion.li key={f.id} className={s.row} data-testid="scan-row"
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={reduced ? fadeOnly : { duration: 0.22, ease: easeOut, delay: i * stagger.list }}>
              <span className={s.rowText}><span className="bodySmBold">{f.title}</span><span className={`bodyXs ${s.muted}`}>{PLACE_LABEL[placeOf(f)]}</span></span>
              <SeverityBadge severity={f.severity} />
            </motion.li>
          ))}
        </AnimatePresence>
        {done && findings.length === 0 && <li className={`bodySm ${s.muted}`}>Nothing new. We keep watching.</li>}
      </ul>

      {done && (
        <motion.div className={s.result} initial={reduced ? { opacity: 0 } : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={reduced ? fadeOnly : { duration: 0.4, ease: easeOut }}>
          <span className={`bodySm ${s.muted}`}>Score</span>
          {scoreBefore !== undefined && scoreBefore !== scoreAfter && <span className={`bodyMd ${s.before}`}>{scoreBefore} →</span>}
          <span className={s.score} data-testid="live-scan-score">{scoreAfter}</span>
          <span className={`bodySmBold ${s[band(scoreAfter)]}`}>{BAND_LABEL[band(scoreAfter)]}</span>
        </motion.div>
      )}
      {done && footer}
    </section>
  );
}
